import React, { useState, useEffect } from "react";
import {
  TextField, Select, MenuItem, FormControl, RadioGroup, FormControlLabel, Radio,
  Checkbox, Typography, Card, CardContent, Button, Box, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, CircularProgress, IconButton, Divider
} from "@mui/material";
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';

const MotorForm = ({ onSubmit }) => {
  const [loading, setLoading] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [formData, setFormData] = useState({
    cc: "1300",
    fiv: "",
    ncb: 0,
    riskOptions: { 
        flood: false, 
        earthquake: false,
        rsd: false, 
        avls: true 
    },
    passengers: 4,
    vehicleNumber: "",
    ownerName: "",
    registrationDoc: null,
    previousInsuranceDoc: null,
    engineNumber: "",
    chassisNumber: "",
    mfgYear: "",
    cc_capacity: "",
    vehicle_class: "",
    isScanned: false
  });

  const [previews, setPreviews] = useState({
    registrationDoc: null,
    previousInsuranceDoc: null
  });

  // --- LOGIC TABLES ---
  const baseTPFromCC = (cc) => {
    const n = parseInt(cc) || 0;
    if (n <= 1300) return 2795;
    if (n <= 1800) return 2873;
    if (n <= 3000) return 2925;
    return 2990;
  };

  const motorLiabilityPremiumFromCC = (cc) => {
    const n = parseInt(cc) || 0;
    if (n <= 1300) return 750;
    if (n <= 1800) return 1250;
    if (n <= 3000) return 1750;
    return 2250;
  };

  const calculatePremium = (data) => {
    const fiv = parseFloat(data.fiv) || 0;
    const BASIC_PREMIUM = baseTPFromCC(data.cc);
    const FLV_ADD = fiv * 0.0165;
    
    // Fixed logic for Optional Risks
    const FLOOD_COST = data.riskOptions.flood ? fiv * 0.0025 : 0;
    const EQ_COST = data.riskOptions.earthquake ? fiv * 0.0025 : 0;
    const RSD_COST = data.riskOptions.rsd ? fiv * 0.005 : 0; 

    const avlsBase = BASIC_PREMIUM + FLV_ADD + FLOOD_COST + EQ_COST + RSD_COST;
    const AVLS = data.riskOptions.avls ? avlsBase * 0.10 : 0;

    const OD_PREMIUM = avlsBase - AVLS;
    const NCB_LESS = OD_PREMIUM * (parseFloat(data.ncb) / 100);
    const SUB_TOTAL = OD_PREMIUM - NCB_LESS;

    const MOTOR_LIABILITY = motorLiabilityPremiumFromCC(data.cc);
    const DRIVER_COST = 150; 
    const PASSENGER_COST = (parseInt(data.passengers) || 0) * 225; 

    const NET_PREMIUM = SUB_TOTAL + MOTOR_LIABILITY + DRIVER_COST + PASSENGER_COST;
    const VAT_AMOUNT = NET_PREMIUM * 0.15;
    const GROSS_PREMIUM = Math.round(NET_PREMIUM + VAT_AMOUNT);

    return {
      BASIC_PREMIUM, FLV_ADD, RSD_COST, FLOOD_COST, EQ_COST, AVLS,
      OD_PREMIUM, NCB_LESS, SUB_TOTAL, MOTOR_LIABILITY,
      PASSENGER_COST, DRIVER_COST, NET_PREMIUM, VAT_AMOUNT, GROSS_PREMIUM
    };
  };

  // --- HANDLERS ---
  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "checkbox") {
      setFormData(prev => ({ 
        ...prev, 
        riskOptions: { ...prev.riskOptions, [name]: checked } 
      }));
    } else if (name === "registrationDoc") {
      const file = files[0];
      if (file) {
        setFormData(prev => ({ ...prev, registrationDoc: file }));
        const objectUrl = URL.createObjectURL(file);
        setPreviews(prev => ({ ...prev, registrationDoc: objectUrl }));
        handleRegistrationScan(file);
      }
    } else if (name === "previousInsuranceDoc") {
      const file = files[0];
      if (file) {
        setFormData(prev => ({ ...prev, previousInsuranceDoc: file }));
        const objectUrl = URL.createObjectURL(file);
        setPreviews(prev => ({ ...prev, previousInsuranceDoc: objectUrl }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Restored missing removeFile function
  const removeFile = (name) => {
    setFormData(prev => ({ ...prev, [name]: null }));
    if (previews[name]) {
      URL.revokeObjectURL(previews[name]);
      setPreviews(prev => ({ ...prev, [name]: null }));
    }
    if (name === "registrationDoc") setFormData(prev => ({ ...prev, isScanned: false }));
  };

  const handleRegistrationScan = async (file) => {
    setLoading(true);
    const uploadData = new FormData();
    uploadData.append("registrationDoc", file);
    try {
    //  const response = await fetch("http://localhost/bgic-store/admin/scan_registrationdoc.php", {
        const response = await fetch("https://www.bgicdigital.com/admin/scan_registrationdoc.php", {
        method: "POST",
        body: uploadData,
      });
      const result = await response.json();
      if (result.success) {
        setFormData(prev => ({
          ...prev,
          vehicleNumber: result.data.reg_no || "",
          engineNumber: result.data.engine_no || "",
          chassisNumber: result.data.chassis_no || "",
          mfgYear: result.data.mfg_year || "",
          cc_capacity: result.data.cc_capacity || "",
          vehicle_class: result.data.vehicle_class || "",          
          isScanned: true
        }));
      }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }; 

  const handleSubmit = (e) => {
    e.preventDefault();
    const calc = calculatePremium(formData);
    onSubmit({
      productType: "Motor Insurance",
      data: { ...formData, ...calc },
    });
  };

  useEffect(() => {
    return () => {
      Object.values(previews).forEach(url => url && URL.revokeObjectURL(url));
    };
  }, [previews]);

  const calc = calculatePremium(formData);

  return (
    <form onSubmit={handleSubmit}>
      

    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 3,
        flexWrap: "wrap",
        gap: 2,
      }}
    >
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Car Insurance (Comprehensive)
        </Typography>
    
        <Typography
          variant="body2"
          sx={{ color: "text.secondary" }}
        >
          Please complete the form below to proceed.
        </Typography>
      </Box>
    
      <Button
        variant="outlined"
        color="secondary"
        onClick={() => setOpenDetails(true)}
        sx={{
          textTransform: "none",
          borderRadius: 2,
          px: 3,
          fontWeight: 500,
        }}
      >
        View Details
      </Button>
    </Box>



      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6">Engine Capacity (CC)</Typography>
          <RadioGroup name="cc" value={formData.cc} onChange={handleChange} row>
            <FormControlLabel value="1300" control={<Radio />} label="Up to 1300 CC" />
            <FormControlLabel value="1800" control={<Radio />} label="1301–1800 CC" />
            <FormControlLabel value="3000" control={<Radio />} label="1801–3000 CC" />
            <FormControlLabel value="3001" control={<Radio />} label="3001 CC+" />
          </RadioGroup>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField label="Vehicle Sum Insured (Tk.)" type="number" name="fiv" value={formData.fiv} onChange={handleChange} fullWidth required sx={{"& .MuiFormLabel-asterisk": {color: "red"}}}/>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField label="Passengers" type="number" name="passengers" value={formData.passengers} onChange={handleChange} fullWidth />
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <Typography variant="subtitle2">No Claim Bonus (NCB)</Typography>
            <Select name="ncb" value={formData.ncb} onChange={handleChange}>
              <MenuItem value={0}>0% (New Policy)</MenuItem>
              <MenuItem value={30}>30%</MenuItem>
              <MenuItem value={40}>40%</MenuItem>
              <MenuItem value={50}>50%</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2">Discounts & Optional Risks</Typography>
          <FormControlLabel control={<Checkbox name="avls" checked={formData.riskOptions.avls} onChange={handleChange} />} label="AVLS Discount (10%)" />
          <FormControlLabel control={<Checkbox name="rsd" checked={formData.riskOptions.rsd} onChange={handleChange} />} label="Riot & Strike (0.5%)" />
          <FormControlLabel control={<Checkbox name="flood" checked={formData.riskOptions.flood} onChange={handleChange} />} label="Flood & Cyclone" />
          <FormControlLabel control={<Checkbox name="earthquake" checked={formData.riskOptions.earthquake} onChange={handleChange} />} label="Earthquake" />
        </Grid>

        {/* --- SCAN SECTION --- */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ color: "red", fontWeight: "bold" }}>Upload Registration Document * {loading && <CircularProgress size={16} sx={{ ml: 1 }} />}</Typography>
          {!previews.registrationDoc ? (
            <input type="file" name="registrationDoc" onChange={handleChange} required style={{ color: 'red', marginTop: '8px' }} />
          ) : (
            <Box sx={{ position: 'relative', border: '1px solid #f44336', mt: 1, p: 1, borderRadius: 1 }}>
              <img src={previews.registrationDoc} alt="Scan" style={{ width: '100%', height: '80px', objectFit: 'contain' }} />
              <IconButton size="small" color="error" onClick={() => removeFile('registrationDoc')} sx={{ position: 'absolute', top: 5, right: 5, bgcolor: 'white' }}><DeleteIcon /></IconButton>
            </Box>
          )}
        </Grid>
        
        {formData.ncb > 0 && (
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ color: "red", fontWeight: "bold" }}>Upload Previous Insurance Document *</Typography>
            {!previews.previousInsuranceDoc ? (
              <input type="file" name="previousInsuranceDoc" onChange={handleChange} required style={{ color: 'red', marginTop: '8px' }} />
            ) : (
              <Box sx={{ position: 'relative', border: '1px solid #f44336', mt: 1, p: 1, borderRadius: 1 }}>
                <img src={previews.previousInsuranceDoc} alt="Scan" style={{ width: '100%', height: '80px', objectFit: 'contain' }} />
                <IconButton size="small" color="error" onClick={() => removeFile('previousInsuranceDoc')} sx={{ position: 'absolute', top: 5, right: 5, bgcolor: 'white' }}><DeleteIcon /></IconButton>
              </Box>
            )}
          </Grid>
        )}

        {formData.isScanned && (
          <>
            <Grid item xs={12} md={6}><TextField label="Reg No" name="vehicleNumber" value={formData.vehicleNumber} onChange={handleChange} fullWidth required sx={{"& .MuiFormLabel-asterisk": {color: "red"}}}/></Grid>
            <Grid item xs={12} md={6}><TextField label="Owner Name" name="ownerName" value={formData.ownerName} onChange={handleChange} fullWidth required sx={{"& .MuiFormLabel-asterisk": {color: "red"}}}/></Grid>
            <Grid item xs={12} md={4}><TextField label="Engine No" name="engineNumber" value={formData.engineNumber} onChange={handleChange} fullWidth required sx={{"& .MuiFormLabel-asterisk": {color: "red"}}}/></Grid>
            <Grid item xs={12} md={4}><TextField label="Chassis No" name="chassisNumber" value={formData.chassisNumber} onChange={handleChange} fullWidth required sx={{"& .MuiFormLabel-asterisk": {color: "red"}}}/></Grid>
            <Grid item xs={12} md={4}><TextField label="CC Capacity" name="cc_capacity" value={formData.cc_capacity} onChange={handleChange} fullWidth required sx={{"& .MuiFormLabel-asterisk": {color: "red"}}}/></Grid>
            <Grid item xs={12} md={4}><TextField label="Vehicle Class" name="vehicle_class" value={formData.vehicle_class} onChange={handleChange} fullWidth /></Grid>
          </>
        )}

        {/* --- COMPUTATION TABLE --- */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mt: 2 }}>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr><th colSpan="4" style={{ textAlign: "center", background: "#d9d9d9", fontWeight: 700, padding: "4px 6px", border: "1px solid #999" }}>PREMIUM COMPUTATION (PRIVATE CAR)</th></tr>
              </thead>
              <tbody>
                <tr><td style={{ padding: "4px 6px", border: "1px solid #999" }}>Basic Premium</td><td width="30" style={{ border: "1px solid #999" }}>Tk.</td><td align="right" style={{ border: "1px solid #999" }}>{calc.BASIC_PREMIUM}</td><td style={{ border: "1px solid #999" }}></td></tr>
                <tr><td style={{ padding: "4px 6px", border: "1px solid #999" }}>Add: 1.65% on F.I.V.</td><td style={{ border: "1px solid #999" }}>Tk.</td><td align="right" style={{ border: "1px solid #999" }}>{calc.FLV_ADD.toFixed(2)}</td><td style={{ border: "1px solid #999" }}></td></tr>
                {calc.RSD_COST > 0 && <tr><td style={{ padding: "4px 6px", border: "1px solid #999" }}>&nbsp;&nbsp;Riot & Strike (0.50% on F.I.V.)</td><td style={{ border: "1px solid #999" }}>Tk.</td><td align="right" style={{ border: "1px solid #999" }}>{calc.RSD_COST.toFixed(2)}</td><td style={{ border: "1px solid #999" }}></td></tr>}
                {calc.FLOOD_COST > 0 && <tr><td style={{ padding: "4px 6px", border: "1px solid #999" }}>&nbsp;&nbsp;Flood & Cyclone (0.25% on F.I.V.)</td><td style={{ border: "1px solid #999" }}>Tk.</td><td align="right" style={{ border: "1px solid #999" }}>{calc.FLOOD_COST.toFixed(2)}</td><td style={{ border: "1px solid #999" }}></td></tr>}
                {calc.EQ_COST > 0 && <tr><td style={{ padding: "4px 6px", border: "1px solid #999" }}>&nbsp;&nbsp;Earthquake (0.25% on F.I.V.)</td><td style={{ border: "1px solid #999" }}>Tk.</td><td align="right" style={{ border: "1px solid #999" }}>{calc.EQ_COST.toFixed(2)}</td><td style={{ border: "1px solid #999" }}></td></tr>}
                <tr><td style={{ padding: "4px 6px", border: "1px solid #999" }}>AVLS Discount (10%)</td><td style={{ border: "1px solid #999" }}>Tk.</td><td align="right" style={{ border: "1px solid #999", color: 'red' }}>-{calc.AVLS.toFixed(2)}</td><td style={{ border: "1px solid #999" }}></td></tr>
                <tr style={{ fontWeight: 'bold' }}><td style={{ padding: "4px 6px", border: "1px solid #999" }}>Own damage Premium</td><td style={{ border: "1px solid #999" }}>Tk.</td><td align="right" style={{ border: "1px solid #999" }}>{calc.OD_PREMIUM.toFixed(2)}</td><td style={{ border: "1px solid #999" }}></td></tr>
                <tr><td style={{ padding: "4px 6px", border: "1px solid #999" }}>Less: NCB {formData.ncb}%</td><td style={{ border: "1px solid #999" }}>Tk.</td><td align="right" style={{ border: "1px solid #999", color: 'red' }}>-{calc.NCB_LESS.toFixed(2)}</td><td style={{ border: "1px solid #999" }}></td></tr>
                <tr style={{ background: "#f9f9f9", fontWeight: "bold" }}><td style={{ padding: "4px 6px", border: "1px solid #999" }}>Sub Total</td><td style={{ border: "1px solid #999" }}>Tk.</td><td align="right" style={{ border: "1px solid #999" }}>{calc.SUB_TOTAL.toFixed(2)}</td><td style={{ border: "1px solid #999" }}></td></tr>
                <tr><td style={{ padding: "4px 6px", border: "1px solid #999" }}>Motor liability premium</td><td style={{ border: "1px solid #999" }}>Tk.</td><td style={{ border: "1px solid #999" }}></td><td align="right" style={{ border: "1px solid #999" }}>{calc.MOTOR_LIABILITY}</td></tr>
                <tr><td style={{ padding: "4px 6px", border: "1px solid #999" }}>Passenger ({formData.passengers})</td><td style={{ border: "1px solid #999" }}>Tk.</td><td style={{ border: "1px solid #999" }}></td><td align="right" style={{ border: "1px solid #999" }}>{calc.PASSENGER_COST}</td></tr>
                <tr><td style={{ padding: "4px 6px", border: "1px solid #999" }}>Paid Driver (1)</td><td style={{ border: "1px solid #999" }}>Tk.</td><td style={{ border: "1px solid #999" }}></td><td align="right" style={{ border: "1px solid #999" }}>{calc.DRIVER_COST}</td></tr>
                <tr style={{ fontWeight: 'bold' }}><td style={{ padding: "4px 6px", border: "1px solid #999" }}>Net Premium</td><td style={{ border: "1px solid #999" }}>Tk.</td><td style={{ border: "1px solid #999" }}></td><td align="right" style={{ border: "1px solid #999" }}>{calc.NET_PREMIUM.toFixed(2)}</td></tr>
                <tr><td style={{ padding: "4px 6px", border: "1px solid #999" }}>Add: VAT 15%</td><td style={{ border: "1px solid #999" }}>Tk.</td><td style={{ border: "1px solid #999" }}></td><td align="right" style={{ border: "1px solid #999" }}>{calc.VAT_AMOUNT.toFixed(2)}</td></tr>
                <tr style={{ fontWeight: "bold", background: "#eee" }}><td style={{ padding: "4px 6px", border: "1px solid #999" }}>Gross Premium :</td><td style={{ border: "1px solid #999" }}>Tk.</td><td style={{ border: "1px solid #999" }}></td><td align="right" style={{ border: "1px solid #999" }}>{calc.GROSS_PREMIUM}</td></tr>
              </tbody>
            </Box>
          </Card>
        </Grid>
      </Grid>
      <Button type="submit" variant="contained" fullWidth sx={{ mt: 3, py: 1.5, fontWeight: 'bold' }}>Add to Cart (Tk. {calc.GROSS_PREMIUM})</Button>

      {/* Terms Dialog */}
      <Dialog open={openDetails} onClose={() => setOpenDetails(false)} maxWidth="md" fullWidth>
        <DialogTitle>Insurance Coverage & Terms</DialogTitle>
        <DialogContent dividers>
                          <Typography variant="subtitle1" gutterBottom><strong>Coverage Details:</strong></Typography>
                          <ul>
                            <li>Accidental Damage, Fire & Theft (1.65% of FIV) — Mandatory coverage</li>
                            <li>Riot & Strike Damage (0.50% of FIV) — Optional</li>
                            <li>Flood & Cyclone (0.25% of FIV) — Optional</li>
                            <li>Earthquake (0.25% of FIV) — Optional</li>
                            <li>Passenger Liability: ৳225 per passenger</li>
                            <li>Driver Liability: ৳150</li>
                          </ul>
                
                          <Typography variant="subtitle1" gutterBottom><strong>Terms & Conditions:</strong></Typography>
                          <ul>
                            <li>NCB applies only for renewals with no claims in the previous year.</li>
                            <li>Coverage is valid for one year from the start date.</li>
                            <li>Insurance coverage begins from the next day of purchase.</li>
                            <li>Excludes deliberate damage, driving without license, and non-disclosed modifications.</li>
                          </ul>
                
                          <Typography variant="subtitle1" gutterBottom><strong>Claiming Process:</strong></Typography>
                          <ol>
                            <li>Inform the insurer within 24 hours of an incident.</li>
                            <li>Submit required documents (claim form, FIR, repair invoice, previous insurance docs).</li>
                            <li>Survey and verification by insurance representative.</li>
                            <li>Claim settlement typically within 15 working days after approval.</li>
                          </ol>
                        </DialogContent>
        <DialogActions><Button onClick={() => setOpenDetails(false)} color="primary" variant="contained">Close</Button></DialogActions>
      </Dialog>
    </form>
  );
};

export default MotorForm;